/**
 * compareRows — what the People-lists card says about each selected list's
 * Compare with Thrive, and which lists the page compares by itself (bug hunt
 * 2026-09-23, F1 + F6; automatic since task 2026-09-24-combine-more-filters,
 * batch 2 — there is no "Compare with Thrive" button any more). Pure, so the
 * rule is testable without rendering the page.
 *
 * A list is worth comparing only when the run can make it CHECKED for the
 * active mode (`CombineSourceState.checked`):
 *
 * - "This search": Compare runs against the list's OWN search. When that
 *   search is the target, the run checks the list directly; when it is
 *   another search, the run finds everyone in Thrive and the server checks
 *   them against the target in the background
 *   (`CombineSourceState.membership_check`, epic
 *   2026-10-01-combine-stability-perf: never in the request, never a new
 *   Compare). So every unchecked list is compared, whatever its search, once
 *   a target is picked — except one the server is still checking
 *   (`target_checking`, it settles by itself) or could not check
 *   (`target_check_failed`): comparing the list again would not help.
 * - "Any search": any succeeded Compare checks a list, so every unchecked
 *   list is compared.
 *
 * Those lists read `pending` until `useAutoCompare` starts them. A list whose
 * Compare is already queued/running (the preview's own `compare.status`, or
 * a run this page started or follows) reads `comparing` and is never started
 * again — unless that run is ABANDONED (`compare.stale`: no heartbeat past
 * the server's reclaim threshold, so its worker died). An abandoned run is
 * not in flight: it does not hold up Generate, and the list is compared
 * again, which the server accepts (same predicate).
 *
 * A run this page started or followed (`progress`) and that:
 *
 * - is still going reads `comparing`. If its worker dies, the poll sees
 *   `CompareResult.stale` and ends it as `failed` (`useCompareSources`), so
 *   the row does not stay "Comparing…" and Generate is not held
 *   (/pr-review iter 1 of PR #965, Agent A §3.1 / Agent C M2).
 * - failed (abandoned included) reads `failed`, and is never `pending`
 *   again: the recruiter retries it by hand ("Try again"), the same rule as
 *   before for any failure.
 * - succeeded reads `compared` only until the preview on screen reflects
 *   it (its `compare.computed_at` is the run's own or later). From then on
 *   the row follows the server's `checked` like any other list, so a
 *   change of target or mode after the run says what the funnel counts
 *   (/pr-review iter 1 of PR #965, Agent C M1). A list the page already
 *   started FOR THE CURRENT TARGET AND MODE (`startedIds`) that still reads
 *   unchecked then is `unchecked`, not `pending`: the page starts a list
 *   once per target and mode (`useAutoCompare`), and a `pending` row it
 *   will not start would hold Generate for good. A new target or mode is a
 *   new chance: there is no once-per-session rule (epic
 *   2026-10-01-combine-stability-perf, sub-task 02). When the run or the preview carries
 *   no `computed_at`, the page cannot tell which run the preview shows, so
 *   the row reads `compared` until the preview says the list is checked
 *   (/pr-review iter 2 of PR #965, Agent C N2 / Agent A §3.4).
 *
 * `states` is filtered to the lists selected NOW, so a preview still on
 * screen from an earlier selection cannot resurrect a removed list.
 *
 * Sub-task 02 of epic 2026-10-01-combine-stability-perf: one row per
 * selected list (not per preview state), and the caller passes only the
 * states of a preview that answered the CURRENT target and mode. A list
 * with no state yet (just added, or the target just changed) reads
 * `evaluating` until its preview lands, instead of what the previous
 * target said. Nothing reaches Thrive before the recruiter confirms a
 * target: with none, every list that is not checked reads `no_target`,
 * whatever the mode, and nothing is `pending` (started).
 */
import type { CombineSourceMembershipError, RemoveInThrive } from "@nu-assist/contracts";

import type { CombineSourceState, ImportSummary } from "../../../clients/imports.client";
import type { CompareSourceProgress } from "../../../features/imports/useCompareSources";

export type CompareRow =
  /** The preview on screen does not answer this list for the current
   *  target and mode yet (it was just added, or the target or mode just
   *  changed): its state is being read again. Never the previous target's. */
  | { importId: string; kind: "evaluating" }
  | { importId: string; kind: "pending" }
  /** A Compare is queued/running on the list. No percentage: the run
   *  reports its contacts in large steps (it read 0% and then 99%, Jose's
   *  QA), so the card counts lists instead (`checkProgress`). */
  | { importId: string; kind: "comparing" }
  | { importId: string; kind: "compared" }
  | { importId: string; kind: "failed" }
  /** This page already started a run that succeeded, and the list is still
   *  not checked for the active mode (another run replaced it, or the list
   *  changed since): offered by hand, never started again by itself. */
  | { importId: string; kind: "unchecked" }
  | { importId: string; kind: "no_target" }
  /** The list's whole-list Compare ran against another search and the
   *  server is checking it against the target search in the background
   *  (`membership_check="pending"`): not checked YET, nothing to start; the
   *  page reads the preview again until it settles. */
  | { importId: string; kind: "target_checking" }
  /** That background check failed (`membership_check="failed"`,
   *  `membership_error` says why): not checked, nothing to start. `retryAt`
   *  (`membership_retry_at`): when a preview starts it again by itself,
   *  absent when no automatic retry is left. `unknownSearch`: the API does
   *  not know the target search yet (`isUnknownTargetSearch`). */
  | {
      importId: string;
      kind: "target_check_failed";
      error: CombineSourceMembershipError | null;
      retryAt?: string;
      unknownSearch?: true;
    };

const IN_FLIGHT = new Set(["queued", "running"]);

/** The lists selected now, as the helpers below read them. */
type SelectedLists = readonly Pick<ImportSummary, "import_id">[];

/** A preview source's own run is in flight: queued/running and not abandoned. */
function previewRunInFlight(s: CombineSourceState): boolean {
  return IN_FLIGHT.has(s.compare?.status ?? "") && !s.compare?.stale;
}

/** The preview on screen already reflects a run this page saw succeed: its
 *  source's last run is that one or a later one. (The two timestamps come
 *  from different fields, `CompareResult.computed_at` and
 *  `ImportCompareState.computed_at`, over the same stored value, so they
 *  are compared as instants, not strings.) Without either time, only a
 *  checked list counts as reflected. */
function previewReflects(s: CombineSourceState, own: CompareSourceProgress): boolean {
  const finished = Date.parse(own.computedAt ?? "");
  const shown = Date.parse(s.compare?.computed_at ?? "");
  if (Number.isNaN(finished) || Number.isNaN(shown)) return s.checked;
  return shown >= finished;
}

export interface CompareRowsArgs {
  /** The lists selected right now. */
  sources: readonly Pick<ImportSummary, "import_id">[];
  /** `CombinePreviewResponse.sources` of a preview that answered the
   *  CURRENT target and mode — `[]` while none has. */
  states: readonly CombineSourceState[];
  mode: NonNullable<RemoveInThrive>;
  /** The target search the recruiter CONFIRMED; `null` before (an
   *  inherited suggestion does not count). */
  targetSearchId: string | null;
  /** `useCompareSources().progress` — runs this page started or follows. */
  progress?: ReadonlyMap<string, CompareSourceProgress>;
  /** The lists the page already started for the current target and mode. */
  startedIds?: ReadonlySet<string>;
}

export function compareRows({
  sources,
  states,
  mode,
  targetSearchId,
  progress,
  startedIds,
}: CompareRowsArgs): CompareRow[] {
  if (mode === "off") return [];
  const stateById = new Map(states.map((s) => [s.import_id, s]));
  const rows: CompareRow[] = [];
  for (const source of sources) {
    const importId = source.import_id;
    const s = stateById.get(importId);
    const own = progress?.get(importId);
    if (IN_FLIGHT.has(own?.status ?? "")) {
      rows.push({ importId, kind: "comparing" });
      continue;
    }
    if (own?.status === "succeeded" && (!s || !previewReflects(s, own))) {
      rows.push({ importId, kind: "compared" });
      continue;
    }
    if (own?.status === "failed") {
      rows.push({ importId, kind: "failed" });
      continue;
    }
    if (!s) {
      rows.push({ importId, kind: "evaluating" });
      continue;
    }
    if (s.checked) continue;
    // `own` here is a succeeded run the preview reflects, or none.
    const startedHere = startedIds?.has(importId) ?? false;
    if (previewRunInFlight(s)) {
      rows.push({ importId, kind: "comparing" });
    } else if (targetSearchId == null) {
      rows.push({ importId, kind: "no_target" });
    } else if (mode === "any") {
      rows.push({ importId, kind: startedHere ? "unchecked" : "pending" });
    } else if (s.membership_check === "pending") {
      rows.push({ importId, kind: "target_checking" });
    } else if (s.membership_check === "failed") {
      rows.push({
        importId,
        kind: "target_check_failed",
        error: s.membership_error ?? null,
        ...(failedCheckRetries(s) && s.membership_retry_at
          ? { retryAt: s.membership_retry_at }
          : {}),
        ...(isUnknownTargetSearch(s) ? { unknownSearch: true as const } : {}),
      });
    } else {
      rows.push({ importId, kind: startedHere ? "unchecked" : "pending" });
    }
  }
  return rows;
}

/** The lists `useAutoCompare` acts on: `start` (pending) and `follow`
 *  (already comparing — a run started elsewhere is followed, not repeated).
 *  A list whose run this page is polling (`progress` in flight) is not
 *  followed again; one whose run here ended and that shows a new run is
 *  (/pr-review iter 2 of PR #965, Agent A §3.1). `progress` is required:
 *  without it every run the page polls would be followed a second time
 *  (/pr-review iter 3, Agent A §2.2). */
export function autoCompareIds(
  rows: readonly CompareRow[],
  progress: ReadonlyMap<string, CompareSourceProgress>
): { start: string[]; follow: string[] } {
  const polled = (id: string) => IN_FLIGHT.has(progress.get(id)?.status ?? "");
  return {
    start: rows.filter((r) => r.kind === "pending").map((r) => r.importId),
    follow: rows
      .filter((r) => r.kind === "comparing" && !polled(r.importId))
      .map((r) => r.importId),
  };
}

/** Every selected list is checked in the preview, and this page saw work
 *  on one of them: a Compare it ran succeeded, or the list sat in
 *  `target_checking` / `pending` here (`seenChecking`). The People-lists card
 *  then stays, with its live heading, to say so (/pr-review iter 2 of PR
 *  #965, Agent C N1). Without it the card, heading included, unmounted the
 *  moment the last list turned checked, so the confirmation was hardly seen
 *  or announced. `seenChecking` is the case a Compare run never covers: ONE
 *  already-compared list that only needed its check against the target ran
 *  no Compare here, so the card vanished (combine-followups 04). */
export function checkedAfterCompare(
  sources: SelectedLists,
  states: readonly CombineSourceState[],
  progress: ReadonlyMap<string, CompareSourceProgress>,
  seenChecking: ReadonlySet<string> = new Set()
): boolean {
  const checked = new Set(states.filter((s) => s.checked).map((s) => s.import_id));
  return (
    sources.length > 0 &&
    sources.every((s) => checked.has(s.import_id)) &&
    sources.some(
      (s) => progress.get(s.import_id)?.status === "succeeded" || seenChecking.has(s.import_id)
    )
  );
}

/** Row kinds that mean "this page is waiting on the list's check". */
const SEEN_CHECKING_KINDS: ReadonlySet<CompareRow["kind"]> = new Set([
  "target_checking",
  "pending",
]);

/** The lists this page saw checking (`target_checking` / `pending`) under
 *  one target and mode (`scopeKey`). Held in state and advanced in an
 *  effect, never written during render (/pr-review iter 1 on PR #1004). */
export interface SeenChecking {
  scopeKey: string;
  ids: ReadonlySet<string>;
}

export const NO_SEEN_CHECKING: SeenChecking = { scopeKey: "", ids: new Set() };

/** `seen` advanced by what is on screen now: another target or mode starts
 *  over (it must not inherit what was seen under this one); otherwise the
 *  lists no longer selected are dropped, so a list deselected and picked
 *  again cannot keep the "checked" card on old evidence, and the lists
 *  `rows` shows checking now are added. Returns `seen` itself when nothing
 *  changed, so a `useState` holder does not churn. */
export function nextSeenChecking(
  seen: SeenChecking,
  scopeKey: string,
  rows: readonly CompareRow[],
  selectedIds: readonly string[]
): SeenChecking {
  const selected = new Set(selectedIds);
  const kept = seen.scopeKey === scopeKey ? [...seen.ids].filter((id) => selected.has(id)) : [];
  const next = new Set(kept);
  for (const r of rows) {
    if (SEEN_CHECKING_KINDS.has(r.kind) && selected.has(r.importId)) next.add(r.importId);
  }
  const unchanged =
    seen.scopeKey === scopeKey &&
    next.size === seen.ids.size &&
    [...next].every((id) => seen.ids.has(id));
  return unchanged ? seen : { scopeKey, ids: next };
}

/** The lists `seen` holds for `scopeKey`: none for another target or mode,
 *  so the render before the effect catches up never reads stale evidence. */
export function idsSeenChecking(seen: SeenChecking, scopeKey: string): ReadonlySet<string> {
  return seen.scopeKey === scopeKey ? seen.ids : NO_SEEN_CHECKING.ids;
}

/** "1 list" / "2 lists". */
function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

/** How many of the selected lists are checked for the active mode against
 *  the confirmed target: every list without a row (`compareRows` gives a
 *  row to each list that is not checked yet). A count of lists, never a
 *  run's percentage, so it only moves when a list's state really changes. */
export function checkProgress(
  rows: readonly CompareRow[],
  sources: SelectedLists
): { checked: number; total: number } {
  return { checked: Math.max(0, sources.length - rows.length), total: sources.length };
}

/** Kinds still on their way to checked: nothing for the recruiter to do.
 *  Not `compared` (a run that succeeded, which the preview on screen does
 *  not show yet): the page holds Generate for it only while the refetch is
 *  in flight ("Updating preview…"); once it has landed and still does not
 *  show the run, nothing would end the wait. */
const IN_PROGRESS: readonly CompareRow["kind"][] = [
  "evaluating",
  "pending",
  "comparing",
  "target_checking",
];

/** "2 lists still checking against Q3 Search…" — the Generate hold while
 *  lists are on their way, also used for the server's 409
 *  `target_check_pending`. */
export function stillCheckingCopy(lists: number, targetLabel: string): string {
  return `${plural(lists, "list")} still checking against ${targetLabel}…`;
}

/** Why Generate waits on the lists' Thrive checks, or `null` when no row
 *  holds it (sub-task 02 of epic 2026-10-01-combine-stability-perf): lists
 *  on their way, then Compare runs that failed, then lists a run here left
 *  unchecked. A list with no target (`no_target`) is the target's own reason,
 *  given before this one. A FAILED check against the target does not hold
 *  Generate: waiting cannot heal `search_not_found` / `source_not_compared`,
 *  and the server settles a check that never answers (`worker_timeout`) and
 *  accepts Generate, keeping that list's people unchecked. `generateNote`
 *  says so instead. The one exception comes first: a target the API does
 *  not know yet (`unknownSearch`), which Generate refuses (422) on the same
 *  searches list (/pr-review iter 6 on PR #993, Agent C I6-2). */
export function generateHold(rows: readonly CompareRow[], targetLabel: string): string | null {
  if (rows.some((r) => r.kind === "target_check_failed" && r.unknownSearch)) {
    return `${targetLabel} isn't available yet — pick another search or try again shortly.`;
  }
  const count = (kinds: readonly CompareRow["kind"][]) =>
    rows.filter((r) => kinds.includes(r.kind)).length;
  const checking = count(IN_PROGRESS);
  if (checking > 0) return stillCheckingCopy(checking, targetLabel);
  const failed = count(["failed"]);
  if (failed > 0) {
    return `Compare failed on ${plural(failed, "list")} — try again, or remove ${failed === 1 ? "it" : "them"}.`;
  }
  const unchecked = count(["unchecked"]);
  if (unchecked > 0) {
    return `${plural(unchecked, "list")} not checked against ${targetLabel} — Compare again, or remove ${unchecked === 1 ? "it" : "them"}.`;
  }
  return null;
}

/** The note beside an ENABLED Generate when a list's check against the
 *  target failed: Generate goes ahead, and that list's people stay unchecked
 *  (nobody is removed as "already in Thrive" through it). `null` with no
 *  failed check. `titleOf` names a list. */
export function generateNote(
  rows: readonly CompareRow[],
  targetLabel: string,
  titleOf: (importId: string) => string
): string | null {
  const failed = rows.filter(
    (r): r is Extract<CompareRow, { kind: "target_check_failed" }> =>
      r.kind === "target_check_failed" && !r.unknownSearch
  );
  if (failed.length === 0) return null;
  const names = failed.map((r) => titleOf(r.importId)).join(", ");
  const tail = `contacts from ${names} won't be checked against this search`;
  if (failed.some((r) => r.error === "search_not_found")) {
    return `Thrive has no search ${targetLabel} — you can generate now; ${tail}. Pick another search to check them.`;
  }
  if (failed.every((r) => r.error === "worker_timeout")) {
    return `Checking against ${targetLabel} is taking too long — you can generate now; ${tail}.`;
  }
  if (failed.some((r) => r.error === "source_not_compared")) {
    return `${plural(failed.length, "list")} could not be checked against ${targetLabel} — you can generate now; ${tail}. Compare it again to check it.`;
  }
  return `${plural(failed.length, "list")} could not be checked against ${targetLabel} — you can generate now; ${tail}.`;
}

export interface CompareHeadingArgs {
  /** `compareRows` for the lists selected now. */
  rows: readonly CompareRow[];
  /** The confirmed target's name (or "search <id>"); `null` with none. */
  targetLabel?: string | null;
  sources: SelectedLists;
  /** `CombinePreviewResponse.sources`. */
  states: readonly CombineSourceState[];
  progress: ReadonlyMap<string, CompareSourceProgress>;
  /** The preview on screen answers the current recipe. */
  previewReady: boolean;
  /** The last ready preview for the lists, mode and target selected now
   *  read "All selected lists checked in Thrive" (`nextAllCheckedKey`). */
  allCheckedHeld: boolean;
  /** Lists this page saw checking under the current target and mode
   *  (`idsSeenChecking`) — see `checkedAfterCompare`. */
  seenChecking?: ReadonlySet<string>;
}

const ALL_CHECKED = "All selected lists checked in Thrive";

/** The Compare card's live heading, from what is on screen NOW: lists being
 *  checked, then failed runs, then lists not checked; with none of those,
 *  "All selected lists checked in Thrive" once the ready preview shows it
 *  (`checkedAfterCompare`). A filter edit cannot change which lists are
 *  checked, so while the preview refetches for the same lists, mode and
 *  target that already read it (`allCheckedHeld`), the sentence stays: no
 *  flicker, no second announcement (/pr-review iter 5 of PR #965, Agent C
 *  N4). Otherwise a run finished that the preview does not show: "updating"
 *  while a read is pending or in flight (a refetch, a shrunk selection),
 *  and "not refreshed yet" once the ready preview still does not show it (a
 *  refetch that failed and kept the old data, or a run with no
 *  `computed_at` whose list still reads unchecked) — nothing is updating
 *  then (iter 5, Agent C N5 / Agent A §3.2). The row stays `compared`
 *  there, not `unchecked`: without a fresh read the page cannot tell that
 *  the run did not check the list (iter 2, Agent C N2). Never what the
 *  heading said before (iter 4, Agent A §2.1 / §3.2, Agent C N1 / N3). */
export function compareHeading({
  rows,
  targetLabel = null,
  sources,
  states,
  progress,
  previewReady,
  allCheckedHeld,
  seenChecking,
}: CompareHeadingArgs): string {
  const count = (kinds: readonly CompareRow["kind"][]) =>
    rows.filter((r) => kinds.includes(r.kind)).length;
  const checking = count(["evaluating", "pending", "comparing", "target_checking"]);
  if (checking > 0) {
    const { checked, total } = checkProgress(rows, sources);
    const against = targetLabel ? `against ${targetLabel}` : "in Thrive";
    return `Checking lists ${against}: ${checked} of ${total} checked`;
  }
  const failed = count(["failed"]);
  if (failed > 0) return `Compare failed on ${plural(failed, "list")}`;
  const noTarget = count(["no_target"]);
  if (noTarget > 0) {
    return `Confirm the target search to check ${plural(noTarget, "list")} in Thrive`;
  }
  const notChecked = count(["target_check_failed", "unchecked"]);
  if (notChecked > 0) return `${plural(notChecked, "list")} not checked in Thrive`;
  // Only `compared` rows, or none, are left.
  if (
    rows.length === 0 &&
    (previewReady || allCheckedHeld) &&
    checkedAfterCompare(sources, states, progress, seenChecking)
  ) {
    return ALL_CHECKED;
  }
  return previewReady ? "Compared — results not refreshed yet" : "Compared — updating the results…";
}

/** The selection key (lists, mode, target) whose ready preview last read
 *  "All selected lists checked in Thrive", for `allCheckedHeld`. A ready
 *  preview sets it (or clears it when it reads anything else); while the
 *  preview is not ready it is kept only for the same key, so a change of
 *  lists, mode or target waits for its own preview, even if changed back
 *  before that preview lands (/pr-review iter 5 of PR #965, Agent C N4). */
export function nextAllCheckedKey(
  prev: string | null,
  {
    previewReady,
    heading,
    selectionKey,
  }: { previewReady: boolean; heading: string; selectionKey: string }
): string | null {
  if (previewReady) return heading === ALL_CHECKED ? selectionKey : null;
  return prev === selectionKey ? prev : null;
}

/** How many selected lists are still being checked against the target
 *  search (`membership_check="pending"`), whatever the removal mode: the
 *  server refuses Generate meanwhile (409 `target_check_pending`), because
 *  the new list inherits that check's Thrive statuses. */
export function pendingTargetChecks(
  sources: SelectedLists,
  states: readonly CombineSourceState[]
): number {
  const selected = new Set(sources.map((s) => s.import_id));
  return states.filter((s) => selected.has(s.import_id) && s.membership_check === "pending").length;
}

/** How the server starts a failed check again (ADR-0027), per
 *  `membership_error`: `retry-soon` and `retry-late` on a TIMER, on a later
 *  preview, while `membership_retry_at` says one is left (the server owns
 *  the back-off and publishes when; a late worker can also adopt a
 *  `worker_timeout` one before that), `never` until the source is compared
 *  again (`search_not_found`, `source_not_compared`) — reading the preview
 *  for those heals nothing, whatever `membership_retry_at` says. The page
 *  waits on a failed check only when both agree (`failedCheckRetries`), and
 *  says "until the list is compared again" for a timer code with no retry
 *  left. A `Record` over the generated contract type, so a new server code
 *  fails the typecheck AND the pin test until it is classified here. */
export const MEMBERSHIP_ERROR_RETRY: Record<
  CombineSourceMembershipError,
  "retry-soon" | "retry-late" | "never"
> = {
  thrive_timeout: "retry-soon",
  thrive_rate_limited: "retry-soon",
  thrive_error: "retry-soon",
  dispatch_failed: "retry-soon",
  worker_timeout: "retry-late",
  search_not_found: "never",
  source_not_compared: "never",
};

/** A failed check a later preview starts again by itself: its code is one
 *  the server retries on a timer and `membership_retry_at` says a retry is
 *  left. `null` there (the retries used up, /pr-review iter 6 on PR #993,
 *  Agent A N14 / Agent C I6-1) means nothing will change until the list is
 *  compared again, so the page stops waiting on it. */
export function failedCheckRetries(s: CombineSourceState): boolean {
  return (
    s.membership_check === "failed" &&
    s.membership_retry_at != null &&
    s.membership_error != null &&
    (MEMBERSHIP_ERROR_RETRY[s.membership_error] ?? "never") !== "never"
  );
}

/** A `search_not_found` the API answered from its own searches list
 *  (`membership_checked_at` null: nothing was checked or stored), as opposed
 *  to Thrive reporting the search missing. Generate refuses that search
 *  (422) until the list knows it — a search created in Thrive since its last
 *  sync — and every later preview looks again, so the page holds Generate
 *  and keeps reading slowly (/pr-review iter 6 on PR #993, Agent C I6-2). */
export function isUnknownTargetSearch(s: CombineSourceState): boolean {
  return (
    s.membership_check === "failed" &&
    s.membership_error === "search_not_found" &&
    s.membership_checked_at == null
  );
}

/** A failed check a later read can change: one with a retry left, or a
 *  target the searches list does not know yet. */
function failedCheckWaits(s: CombineSourceState): boolean {
  return failedCheckRetries(s) || isUnknownTargetSearch(s);
}

/** The earliest `membership_retry_at` of the selected lists' failed checks
 *  that will be retried (epoch ms), or `null` with none: the page reads the
 *  preview again near it, so the retry starts (and shows) on time. */
export function nextTargetCheckRetryAt(
  sources: SelectedLists,
  states: readonly CombineSourceState[]
): number | null {
  const selected = new Set(sources.map((s) => s.import_id));
  let next: number | null = null;
  for (const s of states) {
    if (!selected.has(s.import_id) || !failedCheckRetries(s)) continue;
    const at = Date.parse(s.membership_retry_at ?? "");
    if (!Number.isNaN(at) && (next == null || at < next)) next = at;
  }
  return next;
}

/** "3:04 PM" — when a failed check is tried again (`membership_retry_at`),
 *  in the recruiter's clock. */
export function retryClockLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/** The selected lists whose check is pending, or failed and still read again
 *  (`failedCheckWaits`) — the lists the page is waiting on. Sorted, so it can
 *  key a clock. */
export function activeTargetCheckIds(
  sources: SelectedLists,
  states: readonly CombineSourceState[]
): string[] {
  const selected = new Set(sources.map((s) => s.import_id));
  return states
    .filter(
      (s) => selected.has(s.import_id) && (s.membership_check === "pending" || failedCheckWaits(s))
    )
    .map((s) => s.import_id)
    .sort();
}

/** The selected lists whose check failed. Any of them can turn pending again:
 *  a timer retry claims it, or a late worker adopts a `worker_timeout` one. */
export function failedCheckIds(
  sources: SelectedLists,
  states: readonly CombineSourceState[]
): string[] {
  const selected = new Set(sources.map((s) => s.import_id));
  return states
    .filter((s) => selected.has(s.import_id) && s.membership_check === "failed")
    .map((s) => s.import_id);
}

/** Whether any selected list's check against the target search failed and
 *  a later read can change it (`failedCheckWaits`): the page keeps reading a
 *  preview (slowly) so the retry — or a late worker's adopt — shows. `false`
 *  when every failed check is out of retries (`membership_retry_at` null) or
 *  can only heal by a new Compare: the page stops reading for them. */
export function anyTargetCheckRetrying(
  sources: SelectedLists,
  states: readonly CombineSourceState[]
): boolean {
  const selected = new Set(sources.map((s) => s.import_id));
  return states.some((s) => selected.has(s.import_id) && failedCheckWaits(s));
}

/** Whether any selected list has a Compare queued/running (and not
 *  abandoned) — Generate waits for it while the Thrive removal is on (F9; the
 *  server answers 409 on the same rule). */
export function anyCompareInFlight(
  sources: SelectedLists,
  states: readonly CombineSourceState[],
  progress?: ReadonlyMap<string, CompareSourceProgress>
): boolean {
  const selected = new Set(sources.map((s) => s.import_id));
  const fromPreview = states.some((s) => selected.has(s.import_id) && previewRunInFlight(s));
  const fromPage = [...(progress ?? new Map()).entries()].some(
    ([id, p]) => selected.has(id) && IN_FLIGHT.has(p.status)
  );
  return fromPreview || fromPage;
}
